import { useMeeting, usePubSub } from "@videosdk.live/react-sdk";
import { useAppContext } from "../AppContextDef";
import { useRef } from "react";
import usePubsubFunction from "../utils/usePubsubFunction";
import {
  reduceActiveLimitations,
  deriveQualityStatuses,
  EMPTY_LIMITATIONS,
} from "../utils/participantStats";

/**
 * Client-side pubsub subscriber. The recorder now owns the verification
 * pipeline and is the sole publisher of the verdict channels, so the client
 * must subscribe to them to drive its own ParticipantView UI. It also listens
 * to DELEGATE_API to know when the recorder asks it to run a single API as a
 * bandwidth fallback.
 */
const ClientPubSubHandler = () => {
  const {
    setFaceVerified,
    setAgreedData,
    setFaceDetectError,
    setIsAbortSignal,
    setDelegatedApi,
    setIsRecorderNetworkUnstable,
    recordVerificationOutcome,
  } = useAppContext();

  const pubsubPublishMethod = usePubsubFunction();
  const { publish: qualityLimitationPublish } = usePubSub("QUALITY_LIMITATION");
  const activeLimitationsRef = useRef({ ...EMPTY_LIMITATIONS });

  // The customer's client is the only peer that sees its own outbound quality
  // limitations. Track active types and publish CPU + network status to the
  // recorder's stats panel. A `resolved` event clears its flag, so the statuses
  // fall back to Normal / Stable on their own once the problem passes.
  useMeeting({
    onQualityLimitation: (event) => {
      activeLimitationsRef.current = reduceActiveLimitations(
        activeLimitationsRef.current,
        event,
      );
      const { cpuStatus, networkStatus } = deriveQualityStatuses(
        activeLimitationsRef.current,
      );
      pubsubPublishMethod(
        qualityLimitationPublish,
        "QualityLimitation",
        { persist: true },
        { cpuStatus, networkStatus, active: { ...activeLimitationsRef.current } },
      );
    },
  });

  const latest = (messages) =>
    messages.sort((a, b) => {
      if (a.timestamp > b.timestamp) return -1;
      if (a.timestamp < b.timestamp) return 1;
      return 0;
    })[0];

  usePubSub("FACE_VERIFIED", {
    onMessageReceived: ({ payload }) => {
      setFaceVerified({ isVerified: payload.isVerified, timestamp: Date.now() });
    },
    onOldMessagesReceived: (messages) => {
      const m = latest(messages);
      if (m)
        setFaceVerified({
          isVerified: m.payload.isVerified,
          timestamp: Date.now(),
        });
    },
  });

  usePubSub("AGREED", {
    onMessageReceived: ({ payload }) => {
      setAgreedData(payload.isAgreed);
    },
    onOldMessagesReceived: (messages) => {
      const m = latest(messages);
      if (m) setAgreedData(m.payload.isAgreed);
    },
  });

  usePubSub("FACE_DETECT_ERROR", {
    onMessageReceived: ({ payload }) => {
      setFaceDetectError({ err: payload.err, timestamp: Date.now() });
    },
    onOldMessagesReceived: (messages) => {
      const m = latest(messages);
      if (m) setFaceDetectError({ err: m.payload.err, timestamp: Date.now() });
    },
  });

  usePubSub("ABORT_SIGNAL", {
    onMessageReceived: ({ payload }) => {
      setIsAbortSignal(payload.isAbort);
    },
    onOldMessagesReceived: (messages) => {
      const m = latest(messages);
      if (m) setIsAbortSignal(m.payload.isAbort);
    },
  });

  usePubSub("DELEGATE_API", {
    onMessageReceived: ({ payload }) => {
      setDelegatedApi({ ...payload, timestamp: Date.now() });
    },
    onOldMessagesReceived: (messages) => {
      const m = latest(messages);
      if (m) setDelegatedApi({ ...m.payload, timestamp: Date.now() });
    },
  });

  // Recorder-observed network instability (customer's incoming video bitrate at
  // ~0). Mirrors the recorder's flag so the client can show the blocking dialog
  // and stop/disable the consent recording.
  usePubSub("NETWORK_UNSTABLE", {
    onMessageReceived: ({ payload }) => {
      setIsRecorderNetworkUnstable(!!payload?.isUnstable);
    },
    onOldMessagesReceived: (messages) => {
      const m = latest(messages);
      if (m) setIsRecorderNetworkUnstable(!!m.payload?.isUnstable);
    },
  });

  // Journey-wide fraud counters. Unlike every channel above, this is an EVENT
  // stream, not a state snapshot — each message is a +1, so we fold EVERY
  // message (oldest-first on catch-up), deduped by message id, rather than
  // taking only the latest. On reload the persisted history rebuilds the counts.
  usePubSub("VERIFICATION_OUTCOME", {
    onMessageReceived: (msg) => {
      console.log(
        `%c ◀ recorder %c ${msg?.payload?.apiName} = ${msg?.payload?.outcome} `,
        "color:#fff;background:#2563eb;padding:2px 6px;border-radius:3px 0 0 3px;font-weight:bold",
        "color:#1e3a8a;background:#dbeafe;padding:2px 6px;border-radius:0 3px 3px 0",
      );
      recordVerificationOutcome(
        msg?.payload?.apiName,
        msg?.payload?.outcome,
        msg?.id ?? msg?.timestamp,
      );
    },
    onOldMessagesReceived: (messages) => {
      [...messages]
        .sort((a, b) => {
          if (a.timestamp > b.timestamp) return 1;
          if (a.timestamp < b.timestamp) return -1;
          return 0;
        })
        .forEach((m) =>
          recordVerificationOutcome(
            m?.payload?.apiName,
            m?.payload?.outcome,
            m?.id ?? m?.timestamp,
          ),
        );
    },
  });

  return <></>;
};

export default ClientPubSubHandler;
